/**
 * Extracts utm_source parameter from a URL string
 * @param url - The URL string to extract utm_source from
 * @returns The utm_source value or null if not found
 */
export const getUtmSourceFromUrl = (url: string): string | null => {
  try {
    const urlObject = new URL(url);
    const params = new URLSearchParams(urlObject.search);
    return params.get("utm_source");
  } catch (error) {
    console.error("Failed to parse URL:", error);
    return null;
  }
};

/**
 * Extracts utm_source from marketingChannelData which can be either a URL string or JSON string
 * @param marketingChannelData - URL string or JSON string containing UTM parameters
 * @returns The utm_source value or null if not found
 */
const getUtmSourceFromMarketingData = (
  marketingChannelData: string
): string | null => {
  try {
    // Try to parse as JSON first
    const parsed = JSON.parse(marketingChannelData);
    if (parsed && typeof parsed === "object" && parsed.utm_source) {
      return parsed.utm_source;
    }
  } catch {
    // If JSON parsing fails, try to parse as URL
    try {
      const urlObject = new URL(marketingChannelData);
      const params = new URLSearchParams(urlObject.search);
      const utmSource = params.get("utm_source");
      if (utmSource) {
        return utmSource;
      }
    } catch {
      // Both JSON and URL parsing failed
      console.error(
        "Failed to parse marketingChannelData:",
        marketingChannelData
      );
    }
  }

  return null;
};

/**
 * Gets utm_source from current window location or fallback to marketingChannelData
 * @param marketingChannelData - Optional URL string or JSON string containing marketing channel data from Redux
 * @returns The utm_source value or null if not found
 */
export const getUtmSource = (
  marketingChannelData?: string | null
): string | null => {
  // Try to get utm_source from current URL
  if (typeof window !== "undefined") {
    const currentParams = new URLSearchParams(window.location.search);
    const utmSource = currentParams.get("utm_source");

    if (utmSource) {
      return utmSource;
    }
  }

  // Fallback to marketingChannelData if available
  if (marketingChannelData) {
    return getUtmSourceFromMarketingData(marketingChannelData);
  }

  return null;
};

/**
 * Converts partner data object from API response to array format
 * @param partnerDataObject - Object containing partner data entries (e.g., partnerPhoneNumbers0, partnerPhoneNumbers1)
 * @param keyPrefix - The prefix of keys to filter (e.g., "partnerPhoneNumbers", "medicalServiceDeniedTextPartner")
 * @returns Array of partner data with valid entries only
 */
export const parsePartnerDataToArray = <T extends Record<string, any>>(
  partnerDataObject: Record<string, any> | null | undefined,
  keyPrefix: string
): T[] => {
  if (!partnerDataObject || typeof partnerDataObject !== "object") {
    return [];
  }

  return Object.keys(partnerDataObject)
    .filter((key) => key.startsWith(keyPrefix))
    .map((key) => partnerDataObject[key])
    .filter((item) => {
      if (!item || typeof item !== "object") {
        return false;
      }
      // Check if item has partnerKey and at least one other valid property
      return (
        item.partnerKey &&
        (item.text || item.ctaLabel || Object.keys(item).length > 1)
      );
    }) as T[];
};
